import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, Check } from "lucide-react";
import { motion } from "framer-motion";
import { useAuthOperations, SignupData } from "@/hooks/useAuthOperations";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FBN_CHAPTERS, SEGMENT_OPTIONS } from "@/data/formOptions";

const signupSchema = z.object({
  email: z.string().trim().email("Please enter a valid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string(),
  firstName: z.string().trim().min(1, "First name is required").max(50, "First name is too long"),
  lastName: z.string().trim().min(1, "Last name is required").max(50, "Last name is too long"),
  fbnChapter: z.string().min(1, "Please select your FBN chapter"),
  familyBusiness: z.string().trim().min(1, "Family business name is required").max(100, "Must be less than 100 characters"),
  phoneNumber: z
    .string()
    .trim()
    .min(5, "Please enter a valid phone number")
    .max(30, "Phone number is too long")
    .regex(/^[+\d\s()\-.]+$/, "Use digits, spaces, +, -, ( or )"),
  segment: z.array(z.string()).optional(),
  legalAcceptanceConfirmed: z.literal(true, {
    errorMap: () => ({ message: "You must accept the documents to continue" }),
  }),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});


type SignupFormValues = z.infer<typeof signupSchema>;

export const SignupForm = () => {
  const navigate = useNavigate();
  const { signUp, loading } = useAuthOperations();
  const [success, setSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [resendStatus, setResendStatus] = useState<"idle" | "sending" | "sent">("idle");

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      firstName: "",
      lastName: "",
      fbnChapter: "",
      familyBusiness: "",
      phoneNumber: "",
      segment: [],
      legalAcceptanceConfirmed: false as unknown as true,
    },
  });

  const onSubmit = async (values: SignupFormValues) => {
    const data: SignupData = {
      email: values.email,
      password: values.password,
      firstName: values.firstName,
      lastName: values.lastName,
      fbnChapter: values.fbnChapter,
      familyBusiness: values.familyBusiness,
      phoneNumber: values.phoneNumber,
      segment: values.segment,
    };


    const result = await signUp(data);
    if (result.success) {
      setSubmittedEmail(values.email);
      setSuccess(true);
    }
  };

  const toggleArrayItem = (array: string[] | undefined, item: string): string[] => {
    const current = array || [];
    return current.includes(item)
      ? current.filter((i) => i !== item)
      : [...current, item];
  };

  if (success) {
    const handleResend = async () => {
      if (!submittedEmail || resendStatus !== "idle") return;
      setResendStatus("sending");
      try {
        await supabase.functions.invoke("signup", {
          body: { mode: "resend", email: submittedEmail },
        });
        setResendStatus("sent");
      } catch {
        setResendStatus("idle");
      }
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-8"
      >
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-2xl font-serif font-semibold text-foreground mb-3">
          Check Your Email
        </h3>
        <p className="text-muted-foreground mb-2 max-w-sm mx-auto">
          We've sent a verification link to:
        </p>
        <p className="text-sm font-medium text-foreground mb-4 break-words">
          {submittedEmail}
        </p>
        <p className="text-xs text-muted-foreground mb-6 max-w-sm mx-auto">
          Click the link in the email to activate your account. It may take a minute to arrive - and check your spam folder if you don't see it.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <Button
            variant="outline"
            onClick={handleResend}
            disabled={resendStatus !== "idle"}
          >
            {resendStatus === "sending" ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : resendStatus === "sent" ? (
              "Verification email sent"
            ) : (
              "Resend verification email"
            )}
          </Button>
          <Button variant="ghost" onClick={() => navigate("/")}>
            Back to Home
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-10">
        {/* Section 1: Account Credentials */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
              Account Setup
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email *</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    autoComplete="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password *</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="Min 8 characters"
                      autoComplete="new-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm Password *</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="Repeat password"
                      autoComplete="new-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </section>

        {/* Section 2: Identity */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
              Your Identity
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="John" autoComplete="given-name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Doe" autoComplete="family-name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="fbnChapter"
            render={({ field }) => (
              <FormItem>
                <FormLabel>FBN Chapter *</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your chapter" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {FBN_CHAPTERS.map((chapter) => (
                      <SelectItem key={chapter} value={chapter}>
                        {chapter}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="familyBusiness"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Family Business *</FormLabel>
                <FormControl>
                  <Input placeholder="Name of your family business" autoComplete="organization" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone (WhatsApp) Number *</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+39 333 1234567" autoComplete="tel" {...field} />
                </FormControl>
                <p className="mt-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
                  Heads up: once approved, we'll add this number to our members-only WhatsApp community group. It will also be shared with other members when you accept a connection request from them.
                </p>
                <FormMessage />
              </FormItem>
            )}
          />


        </section>

        {/* Section 3: Role */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
              Your Role
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <FormField
            control={form.control}
            name="segment"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Role(s)</FormLabel>
                <FormDescription>Select all that apply</FormDescription>
                <div className="flex flex-wrap gap-2 mt-2">
                  {SEGMENT_OPTIONS.map((segment) => {
                    const isSelected = field.value?.includes(segment);
                    return (
                      <button
                        key={segment}
                        type="button"
                        onClick={() => field.onChange(toggleArrayItem(field.value, segment))}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          isSelected
                            ? "bg-primary text-primary-foreground shadow-soft"
                            : "bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border"
                        }`}
                      >
                        {segment}
                      </button>
                    );
                  })}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </section>

        <div className="pb-4" />

        {/* Sticky Submit Footer */}
        <div
          className="sticky bottom-0 z-10 -mx-6 px-6 sm:-mx-8 sm:px-8 pt-4 pb-4 bg-card border-t border-border/50 space-y-4"
          style={{ boxShadow: '0 -4px 12px rgba(0,0,0,0.06)' }}
        >
          <FormField
            control={form.control}
            name="legalAcceptanceConfirmed"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value === true}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="text-sm font-normal cursor-pointer">
                    I confirm I am eligible for FBN membership, and I agree to the{" "}
                    <a href="/legal/fbn-international-terms-of-use" target="_blank" rel="noopener noreferrer" className="underline text-primary hover:text-primary/80 font-medium">
                      FBN International Terms of Use
                    </a>
                    , the{" "}
                    <a href="/legal/fbn-international-privacy-notice" target="_blank" rel="noopener noreferrer" className="underline text-primary hover:text-primary/80 font-medium">
                      FBN International Privacy Notice
                    </a>
                    , the{" "}
                    <a href="/legal/supplementary-terms-of-use" target="_blank" rel="noopener noreferrer" className="underline text-primary hover:text-primary/80 font-medium">
                      FBN Entrepreneurs Supplementary Terms of Use
                    </a>
                    , the{" "}
                    <a href="/legal/supplementary-privacy-notice" target="_blank" rel="noopener noreferrer" className="underline text-primary hover:text-primary/80 font-medium">
                      FBN Entrepreneurs Supplementary Privacy Notice
                    </a>
                    , and the{" "}
                    <a href="/legal/code-of-conduct" target="_blank" rel="noopener noreferrer" className="underline text-primary hover:text-primary/80 font-medium">
                      FBN Entrepreneurs Code of Conduct
                    </a>
                    .
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="w-full py-6 text-lg"
            disabled={loading || form.watch("legalAcceptanceConfirmed") !== true}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Creating your account...
              </>
            ) : (
              "Join the Community"
            )}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            By signing up, you agree to our community guidelines. You can complete the rest of your profile after logging in.
          </p>
        </div>
      </form>
    </Form>
  );
};
